export interface IUrl {
    url: string;
    path: string;
}

export interface IUrlObject {
    hash?: string;
    host?: string;
    hostname?: string;
    href?: string;
    origin?: string;
    pathname?: string;
    port?: string;
    protocol?: string;
    search?: string;
    routePrefix?: string
}

const SLASH = '/';

export class Url {

    public static getCurrent(): string {
        return window.location.href;
    }

    /**
     * Parse a url so that its components can be accessed individually, from:
     * http://stackoverflow.com/questions/6644654/ but modified so it doesn't
     * retain the HTML anchor elements (no orphaned DOM nodes).
     *
     * @param {String} path
     * @returns {IUrlObject}
     */
    public static parse(path: string): IUrlObject {

        let a = document.createElement('a');
        a.href = path;

        let obj: IUrlObject = {
            hash: '',
            host: '',
            hostname: '',
            href: '',
            origin: '',
            pathname: '',
            port: '',
            protocol: '',
            search: ''
        };

        Object.keys(obj).forEach((key) => {
            obj[key] = a[key];
        });

        return obj;
    }

    public static parseSearch(search: string): any {
        let result = {};
        search = search.replace('?', '');
        search.split('&').forEach((str) => {
            let kv = str.split('=');
            let key = kv[0];
            let val = kv[1];
            if (key && val) {
                result[key] = val;
            }
        });
        return result;
    }

    /**
     * Build a url string from an object of url properties.
     *
     * @param {IUrlObject} obj
     * @returns {string}
     */
    public static build(obj: IUrlObject) {

        obj.search = (typeof obj.search === 'object')
            ? Url.param(obj.search)
            : obj.search;

        return [
            obj.origin,
            obj.pathname,
            obj.search,
            obj.hash
        ].join('');
    }

    /**
     * Join all arguments together into a URL string. Removes duplicate slashes
     * (but leaves the double slashes in a protocol, eg: http://foo/bar/baz).
     *
     * @param args
     * @returns {String}
     */
    public static join(...args): string {
        return Url.cleanSlashes(args.join(SLASH));
    }

    /**
     * Serialize an object into a URL-friendly key1=val1&key2=val2 string.
     *
     * @param {Object} obj
     * @returns {String}
     */
    public static param(obj: Object): string {
        let pairs = Object.keys(obj).map((key) => {
            let k = encodeURIComponent(key);
            let v = encodeURIComponent(obj[key]);
            return k + '=' + v;
        });
        let str = pairs.join('&');
        return (str) ? '?' + str : '';
    }

    /**
     * Create an absolute URL from a given path.  If the path is already
     * absolute then it is returned as it is. Otherwise the path is made
     * absolute using a base URL or current URL if base is not provided.
     *
     * @param {String} path
     * @param {String} [base]
     * @returns {String}
     */
    public static absolute(path: string, base = Url.getCurrent()): string {
        let isAbsolute = (path.substr(0, 4) === 'http');
        return (isAbsolute) ? path : Url.join(base, path);
    }

    /**
     * Wrap a URL in slashes, ensuring no double slashes. Is careful not
     * to destroy search parameters.
     *
     * Example:
     * Url.wrapSlashes('foo/bar?baz=123'); -> '/foo/bar/?baz=123'
     *
     * @param {String} url
     * @param {Boolean} last
     * @param {Boolean} first
     * @returns {string}
     */
    public static wrapSlashes(url: string, last = true, first = true) {
        let split = url.split('?');
        let search = split[1];
        url = split[0];
        url = (first && (url.substr(0, 4) !== 'http')) ? SLASH + url : url;
        url = (last) ? url + SLASH : url;
        url = (search) ? url + '?' + search : url;
        return Url.cleanSlashes(url);
    }

    /**
     * Remove double slashes from a url except when used in a protocol (https://).
     *
     * @param {String} url
     * @returns {String}
     */
    public static cleanSlashes(url): string {
        return url
            .replace(/([^:]\/)\/+/g, '$1')  // remove from middle
            .replace(/^\/\//, SLASH)        // remove from beginning
            .replace(/\/\/$/, SLASH);       // remove from end
    }

}